import {FC, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {App, Avatar, Badge, Button, ConfigProvider, Empty, Input, List, Space, Tag, Typography, theme} from "antd";
import {SendOutlined, UserOutlined} from "@ant-design/icons";
import AxiosServices from "../../Services/axiosServices";
import {useAuth} from "../../config/AuthContext";
import {useChatEvents} from "../../hooks/useChatEvents";

const ACCENT = '#203923';
const {Text, Title} = Typography;

type Channel = {
    idt: number;
    usrLow_idt: number;
    usrHigh_idt: number;
    initiator_idt: number;
    state: 'PENDING' | 'ACCEPTED' | 'REFUSED' | 'BLOCKED';
    decidedAt: string | null;
    usrLow_username: string;
    usrHigh_username: string;
    unreadCount: number | string;
    lastBody: string | null;
    lastAt: string | null;
};

type ChatMessageRow = {
    idt: number;
    channel_idt: number;
    sender_idt: number;
    body: string;
    createdAt: string;
    readAt: string | null;
};

const ChatScreen: FC = () => {
    const {authUser, isLoggedIn} = useAuth();
    const {message} = App.useApp();
    const me = authUser?.idt;

    const [channels, setChannels] = useState<Channel[]>([]);
    const [activeIdt, setActiveIdt] = useState<number | null>(null);
    const [messages, setMessages] = useState<ChatMessageRow[]>([]);
    const [draft, setDraft] = useState('');
    const [sending, setSending] = useState(false);
    const bottomRef = useRef<HTMLDivElement>(null);

    const refreshChannels = useCallback(() => {
        if (!isLoggedIn) return;
        AxiosServices.chatListChannels()
            .then(setChannels)
            .catch((err) => console.error('chatListChannels failed', err));
    }, [isLoggedIn]);

    const refreshMessages = useCallback((channel_idt: number) => {
        AxiosServices.chatListMessages(channel_idt)
            .then((rows) => setMessages(rows))
            .catch((err) => console.error('chatListMessages failed', err));
    }, []);

    useEffect(() => { refreshChannels(); }, [refreshChannels]);

    useEffect(() => {
        if (activeIdt == null) {
            setMessages([]);
            return;
        }
        refreshMessages(activeIdt);
        AxiosServices.chatMarkRead(activeIdt).catch(() => undefined);
        refreshChannels();
    }, [activeIdt, refreshMessages, refreshChannels]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({behavior: 'smooth'});
    }, [messages]);

    const token = isLoggedIn ? localStorage.getItem('token') : null;
    useChatEvents(token, {
        onMessage: (evt) => {
            if (evt.channel_idt === activeIdt) {
                refreshMessages(activeIdt);
                AxiosServices.chatMarkRead(activeIdt).catch(() => undefined);
            }
            refreshChannels();
        },
        onResponse: () => refreshChannels(),
        onRequest: () => refreshChannels(),
    });

    const channelsSorted = useMemo(() => {
        return [...channels].sort((a, b) => {
            const at = a.lastAt ? new Date(a.lastAt).getTime() : 0;
            const bt = b.lastAt ? new Date(b.lastAt).getTime() : 0;
            return bt - at;
        });
    }, [channels]);

    const activeChannel = channelsSorted.find((c) => c.idt === activeIdt) ?? null;
    const otherName = (c: Channel) => (me === c.usrLow_idt ? c.usrHigh_username : c.usrLow_username);
    const isMine = (row: ChatMessageRow) => me != null && Number(row.sender_idt) === Number(me);

    const send = async () => {
        if (!activeIdt || !draft.trim()) return;
        setSending(true);
        try {
            await AxiosServices.chatSendMessage(activeIdt, draft.trim());
            setDraft('');
            refreshMessages(activeIdt);
            refreshChannels();
        } catch (err: any) {
            const msg = err?.response?.data?.message ?? err?.message ?? 'erreur inconnue';
            message.error(`Échec envoi : ${msg}`);
        } finally {
            setSending(false);
        }
    };

    if (!isLoggedIn) {
        return (
            <div style={{padding: 24}}>
                <Empty description="Connecte-toi pour accéder au chat."/>
            </div>
        );
    }

    return (
        <div style={{height: '100%', display: 'flex', background: ACCENT}}>
            <aside style={{
                width: 280,
                minWidth: 240,
                maxWidth: 360,
                borderRight: '1px solid rgba(255,255,255,0.1)',
                overflowY: 'auto',
            }}>
                <div style={{padding: 12, borderBottom: '1px solid rgba(255,255,255,0.1)'}}>
                    <Title level={5} style={{color: '#fff', margin: 0}}>Discussions</Title>
                </div>
                <ConfigProvider theme={{algorithm: theme.darkAlgorithm}}>
                    {channelsSorted.length === 0 ? (
                        <Empty
                            description={<span style={{color: 'rgba(255,255,255,0.55)'}}>Aucune discussion.</span>}
                            style={{padding: 24}}
                        />
                    ) : (
                        <List
                            dataSource={channelsSorted}
                            renderItem={(c) => {
                                const name = otherName(c);
                                const unread = Number(c.unreadCount) || 0;
                                const active = c.idt === activeIdt;
                                const stateTag = c.state === 'PENDING' ? (
                                    <Tag color={c.initiator_idt === me ? 'blue' : 'orange'}>
                                        {c.initiator_idt === me ? 'Envoyée' : 'En attente'}
                                    </Tag>
                                ) : null;
                                return (
                                    <List.Item
                                        onClick={() => c.state === 'ACCEPTED' && setActiveIdt(c.idt)}
                                        style={{
                                            cursor: c.state === 'ACCEPTED' ? 'pointer' : 'default',
                                            padding: '8px 12px',
                                            background: active ? 'rgba(255,255,255,0.08)' : undefined,
                                            borderLeft: active ? '3px solid #66bb6a' : '3px solid transparent',
                                        }}
                                    >
                                        <List.Item.Meta
                                            avatar={
                                                <Badge count={unread} size="small" offset={[-4, 4]}>
                                                    <Avatar icon={<UserOutlined/>}/>
                                                </Badge>
                                            }
                                            title={
                                                <Space size={6}>
                                                    <span style={{color: '#fff'}}>{name}</span>
                                                    {stateTag}
                                                </Space>
                                            }
                                            description={
                                                <Text style={{color: 'rgba(255,255,255,0.55)', fontSize: 12}} ellipsis>
                                                    {c.lastBody ?? (c.state === 'PENDING' ? 'En attente de réponse…' : 'Aucun message.')}
                                                </Text>
                                            }
                                        />
                                    </List.Item>
                                );
                            }}
                        />
                    )}
                </ConfigProvider>
            </aside>

            <main style={{flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0}}>
                {!activeChannel || activeChannel.state !== 'ACCEPTED' ? (
                    <div style={{flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                        <Empty
                            description={
                                <span style={{color: 'rgba(255,255,255,0.55)'}}>
                                    Sélectionne une discussion acceptée pour échanger.
                                </span>
                            }
                        />
                    </div>
                ) : (
                    <>
                        <div style={{padding: 12, borderBottom: '1px solid rgba(255,255,255,0.1)'}}>
                            <Title level={5} style={{color: '#fff', margin: 0}}>{otherName(activeChannel)}</Title>
                        </div>
                        <div style={{flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 6}}>
                            {messages.map((m) => {
                                const mine = isMine(m);
                                return (
                                    <div
                                        key={m.idt}
                                        style={{
                                            alignSelf: mine ? 'flex-end' : 'flex-start',
                                            maxWidth: '70%',
                                            padding: '6px 10px',
                                            borderRadius: 10,
                                            background: mine ? 'rgba(102,187,106,0.25)' : 'rgba(255,255,255,0.08)',
                                            color: '#fff',
                                        }}
                                    >
                                        <div style={{whiteSpace: 'pre-wrap', wordBreak: 'break-word'}}>{m.body}</div>
                                        <div style={{fontSize: 10, opacity: 0.7, marginTop: 2}}>
                                            {new Date(m.createdAt).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
                                        </div>
                                    </div>
                                );
                            })}
                            <div ref={bottomRef}/>
                        </div>
                        <div style={{padding: 12, borderTop: '1px solid rgba(255,255,255,0.1)', display: 'flex', gap: 8}}>
                            <Input.TextArea
                                value={draft}
                                onChange={(e) => setDraft(e.target.value)}
                                placeholder="Écris ton message…"
                                autoSize={{minRows: 1, maxRows: 4}}
                                onPressEnter={(e) => {
                                    if (!e.shiftKey) { e.preventDefault(); send(); }
                                }}
                                style={{background: 'rgba(255,255,255,0.06)', color: '#fff', borderColor: 'rgba(255,255,255,0.15)'}}
                            />
                            <Button
                                type="primary"
                                icon={<SendOutlined/>}
                                onClick={send}
                                loading={sending}
                                disabled={!draft.trim()}
                            />
                        </div>
                    </>
                )}
            </main>
        </div>
    );
};

export default ChatScreen;
